import React, { useState, useCallback } from 'react'
import {
  Box,
  Snackbar,
  Paper,
  Typography,
  IconButton,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Dialog,
  Button,
  TablePagination,
  TextField,
  Grid,
} from '@mui/material'
import MuiAlert from '@mui/material/Alert'

import API from '@aws-amplify/api'
import { useDispatch, useSelector } from 'react-redux'
import ClearIcon from '@mui/icons-material/Clear'
import { IReduxState } from 'interfaces/IReduxState'
import ConfirmDialog from 'components/confirmDialog'
import {
  IProduction,
  IProductionBatch,
  Production,
} from 'interfaces/IProduction'
import {
  STOCK_CARD_TYPE_HALF_COMPLETE,
  STOCK_CARD_TYPE_PACKAGING,
  STOCK_CARD_TYPE_PREMIX,
  VALUE_OUT,
} from 'enums/stockCard'
import LoadBatchAutocomplete from 'shared/autocomplete/loadBatchAutocomplete'
import { getStockCardDescription, cleanCorruptedString } from './stockCardUtils'
import { IBatch } from 'interfaces/IPremix'
import { formatTimestamp } from 'shared/dateFormatter'
import { formatDecimalAuto } from 'shared/numberFormatter'
import loadingGif from '../../bobana stok.gif'
import LoadingIndicator from 'components/shared/LoadingIndicator'

export default function HalfCompleteProductionList() {
  const type = STOCK_CARD_TYPE_HALF_COMPLETE
  const user = useSelector((state: IReduxState) => state.currentUser || {})
  const [productionData, setProductionData] = React.useState<IProduction>(
    new Production({})
  )
  const [productions, setProductions] = React.useState<IProduction[]>([])
  const [isComplete, setIsComplete] = React.useState(false)
  const [isFailureSnackbarOpen, setIsFailureSnackbarOpen] =
    React.useState(false)
  const [isSuccessSnackbarOpen, setIsSuccessSnackbarOpen] =
    React.useState(false)
  const [isLoading, setIsLoading] = React.useState<boolean>(false)
  const [alertMessage, setAlertMessage] = React.useState('')
  const [isConfirmingPremixRequest, setIsConfirmingPremixRequest] =
    React.useState(false)
  const [isConfirmingPackagingRequest, setIsConfirmingPackagingRequest] =
    React.useState(false)
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = React.useState(false)
  const [
    isRequestingAdditionalPremixOpen,
    setIsRequestingAdditionalPremixOpen,
  ] = React.useState(false)
  const [
    isRequestingAdditionalPackagingOpen,
    setIsRequestingAdditionalPackagingOpen,
  ] = React.useState(false)
  const [additionalPackagingAmount, setAdditionalPackagingAmount] = useState('')
  const [additionalPackagingReason, setAdditionalPackagingReason] = useState('')
  const [additionalPremixAmount, setAdditionalPremixAmount] = useState('')
  const [additionalPremixReason, setAdditionalPremixReason] = useState('')
  const [selectedBatch, setSelectedBatch] = useState<IBatch | null>(null)
  const [selectedProductionBatch, setSelectedProductionBatch] =
    useState<IProductionBatch | null>(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [totalCount, setTotalCount] = useState(0)
  const [searchTerm, setSearchTerm] = useState('')
  const [additionalSoInfo, setAdditionalSoInfo] = useState<{
    [key: number]: string
  }>({})
  const fetchedSoIds = React.useRef(new Set<number>())

  const handleSnackbarClose = (): void => {
    setIsFailureSnackbarOpen(false)
    setIsSuccessSnackbarOpen(false)
  }

  const getProductions = useCallback((
    isCompleteParam: boolean,
    pageParam: number,
    pageSizeParam: number,
    searchTermParam: string
  ) => {
    setIsLoading(true)
    API.get('products', '/production/getProductions', {
      queryStringParameters: {
        type,
        isComplete: isCompleteParam,
        page: pageParam + 1,
        pageSize: pageSizeParam,
        searchTerm: searchTermParam,
      },
    })
      .then((response) => {
        setProductions(response.data)
        setTotalCount(response.totalCount)
      })
      .catch((error) => {
        setIsFailureSnackbarOpen(true)
        setAlertMessage('Gagal memuat form produksi')
        console.error(error.response)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [type])

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
    getProductions(isComplete, newPage, rowsPerPage, searchTerm)
  }

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10)
    setRowsPerPage(newRowsPerPage)
    setPage(0)
    getProductions(isComplete, 0, newRowsPerPage, searchTerm)
  }

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchTerm = event.target.value
    setSearchTerm(newSearchTerm)
    setPage(0)
    getProductions(isComplete, 0, rowsPerPage, newSearchTerm)
  }

  const handleDeleteProduction = () => {
    const productionIdToDelete = productionData.productionId

    setIsLoading(true)
    const apiCall = isComplete
      ? API.patch('products', `/production/cancelProduction/${productionIdToDelete}`, {})
      : API.del(
        'products',
        `/production/deleteProduction/${productionIdToDelete}`,
        {}
      )

    apiCall
      .then(() => {
        setAlertMessage(
          isComplete
            ? `Berhasil membatalkan status SELESAI untuk ${productionData.productionDisplayId}`
            : `Berhasil menghapus PERMINTAAN STJ ${productionData.productionDisplayId}`
        )
        setIsSuccessSnackbarOpen(true)
        getProductions(isComplete, page, rowsPerPage, searchTerm)
      })
      .catch((error) => {
        const errorMessage =
          error?.response?.data?.message || 'Gagal menghapus form produksi'
        setAlertMessage(errorMessage)
        setIsFailureSnackbarOpen(true)
        console.error(error.response)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const handleCreatePremixRequest = () => {
    API.post('products', `/premix/createStockCard`, {
      body: {
        premixId: productionData.premixId,
        quantity: productionData.premixQuantity,
        inOutDirection: VALUE_OUT,
        isConfirmed: false,
        productionId: productionData.productionId,
        createdBy: user.username,
        type: STOCK_CARD_TYPE_PREMIX,
        outgoingDestination: productionData.productionDisplayId,
        batchNumber: selectedProductionBatch?.batchNumber,
        stockCardBatches: [selectedProductionBatch],
        notes: productionData.notes || '',
      },
    })
      .then(() => {
        setAlertMessage('Berhasil membuat permintaan ruahan.')
        setIsSuccessSnackbarOpen(true)
        getProductions(isComplete, page, rowsPerPage, searchTerm)
      })
      .catch((error) => {
        setAlertMessage(
          error?.response?.data?.message || 'Gagal membuat kartu stok.'
        )
        setIsFailureSnackbarOpen(true)
        console.error(error.response)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const handleCreatePackagingRequest = () => {
    setIsLoading(true)
    API.post('products', `/premix/createStockCard`, {
      body: {
        packagingId: productionData.packagingId,
        quantity: selectedProductionBatch?.packagingAmount,
        inOutDirection: VALUE_OUT,
        productionId: productionData.productionId,
        isConfirmed: false,
        createdBy: user.username,
        batchNumber: selectedProductionBatch?.batchNumber,
        type: STOCK_CARD_TYPE_PACKAGING,
        outgoingDestination: productionData.productionDisplayId,
        notes: productionData.notes || '',
      },
    })
      .then(() => {
        setAlertMessage('Berhasil membuat permintaan kemasan.')
        setIsSuccessSnackbarOpen(true)
        getProductions(isComplete, page, rowsPerPage, searchTerm)
      })
      .catch((error) => {
        setAlertMessage(
          error?.response?.data?.message || 'Gagal membuat kartu stok.'
        )
        setIsFailureSnackbarOpen(true)
        console.error(error.response)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const getHalfCompletePremixButtonText = (
    production: IProduction,
    batchNumber: string
  ) => {
    const stockCard = production.stockCards.find(
      (stockCard) =>
        stockCard.type === STOCK_CARD_TYPE_PREMIX &&
        stockCard.batchNumber === batchNumber
    )
    if (!stockCard) {
      return 'Minta Keluarkan Stok Ruahan'
    }
    if (stockCard.isConfirmed) {
      return 'Telah Dikeluarkan, Tunggu Filling'
    }
    return stockCard.stockCardDisplayId
      ? `Tunggu Bets Ruahan Dikeluarkan (Kartu Stok: ${stockCard.stockCardDisplayId})`
      : 'Tunggu Bets Ruahan Dikeluarkan'
  }

  const getHalfCompletePackagingButtonText = (
    production: IProduction,
    batchNumber: string
  ) => {
    const stockCard = production.stockCards.find(
      (stockCard) =>
        stockCard.type === STOCK_CARD_TYPE_PACKAGING &&
        stockCard.batchNumber === batchNumber
    )
    if (!stockCard) {
      return 'Minta Keluarkan Stok Kemasan'
    }
    if (stockCard.isConfirmed) {
      return 'Telah Dikeluarkan, Tunggu Filling'
    }
    return stockCard.stockCardDisplayId
      ? `Tunggu Kemasan Dikeluarkan (Kartu Stok: ${stockCard.stockCardDisplayId})`
      : 'Tunggu Kemasan Dikeluarkan'
  }

  const handleRequestAdditionalPackaging = async () => {
    setIsLoading(true)
    try {
      await API.post('products', `/production/requestAdditionalPackaging`, {
        body: {
          productionId: productionData.productionId,
          additionalPackagingAmount,
          additionalPackagingReason,
        },
      })
      setAlertMessage('Berhasil meminta kemasan tambahan.')
      setIsSuccessSnackbarOpen(true)
      setIsRequestingAdditionalPackagingOpen(false)
      setProductionData(new Production({}))
    } catch (error) {
      setAlertMessage('Gagal meminta kemasan tambahan.')
      setIsFailureSnackbarOpen(true)
      console.error((error as any)?.response || error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleRequestAdditionalPremix = async () => {
    setIsLoading(true)
    try {
      await API.post('products', `/production/requestAdditionalPremix`, {
        body: {
          productionId: productionData.productionId,
          selectedBatch: selectedBatch?.batchNumber,
          additionalPremixAmount,
          additionalPremixReason,
        },
      })
      setAlertMessage('Berhasil meminta ruahan tambahan.')
      setIsSuccessSnackbarOpen(true)
      setIsRequestingAdditionalPremixOpen(false)
      setProductionData(new Production({}))
    } catch (error) {
      setAlertMessage('Gagal meminta ruahan tambahan.')
      setIsFailureSnackbarOpen(true)
      console.error((error as any)?.response || error)
    } finally {
      setIsLoading(false)
    }
  }

  const dispatch = useDispatch()
  React.useEffect(() => {
    dispatch({
      type: 'SET_PAGE_TITLE',
      payload: 'Dafar Permintaan Bikin STJ',
    })
  }, [dispatch])

  const checkIfStockCardExists = (
    production: IProduction,
    stockCardType: string,
    batchNumber: string,
    direction: string
  ) => {
    return production.stockCards.some(
      (stockCard) =>
        stockCard.type === stockCardType &&
        stockCard.batchNumber === batchNumber &&
        stockCard.inOutDirection === direction
    )
  }

  React.useEffect(() => {
    setProductions([])
    setTotalCount(0)
    setPage(0)
    getProductions(isComplete, 0, rowsPerPage, searchTerm)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isComplete, getProductions])

  React.useEffect(() => {
    if (productions.length === 0) return

    productions.forEach((production) => {
      const hasDisplayId =
        production.salesOrderDisplayId ||
        production.salesOrderNumber ||
        production.otDisplayId

      const salesOrderId = production.salesOrderId

      if (
        !hasDisplayId &&
        salesOrderId &&
        !fetchedSoIds.current.has(salesOrderId)
      ) {
        fetchedSoIds.current.add(salesOrderId)

        API.get('products', '/salesOrders/getSalesOrder', {
          queryStringParameters: { salesOrderId },
        })
          .then((response) => {
            if (
              response &&
              response.salesOrders &&
              response.salesOrders.length > 0
            ) {
              const so = response.salesOrders[0]
              const displayId = so.salesOrderDisplayId || so.otDisplayId
              if (displayId) {
                setAdditionalSoInfo((prev) => ({
                  ...prev,
                  [salesOrderId]: displayId,
                }))
              }
            }
          })
          .catch((err) => {
            console.error('Failed to fetch SO info for', salesOrderId, err)
          })
      }
    })
  }, [productions])

  return (
    <Box>
      <React.Fragment>
        <Box mt={3} />
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center" style={{ flexGrow: 1 }}>
            <TextField
              label="Cari STJ / Nama Produk"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={handleSearchChange}
              style={{ width: 300 }}
            />
          </Box>
          <Box display="flex">
            <Button
              variant={!isComplete ? 'contained' : 'outlined'}
              color="primary"
              onClick={() => setIsComplete(false)}
              style={{ marginRight: 8 }}
            >
              Aktif
            </Button>
            <Button
              variant={isComplete ? 'contained' : 'outlined'}
              color="primary"
              onClick={() => setIsComplete(true)}
            >
              Riwayat
            </Button>
          </Box>
        </Box>
      </React.Fragment>
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Kode Permintaan STJ</TableCell>
                <TableCell>Bets Ruahan</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell>Hapus</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading && productions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Box py={10}>
                      <LoadingIndicator gifSrc={loadingGif} alt="loading" size={150} />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                productions.map((production) => (
                  <TableRow key={production.productionId}>
                    <TableCell>
                      <Typography>{production.productionDisplayId}</Typography>
                      <Typography>
                        {formatTimestamp('id-ID', production.createdAt)}
                      </Typography>
                      {(production.salesOrderDisplayId ||
                        production.salesOrderNumber ||
                        production.otDisplayId ||
                        (production.salesOrderId !== null &&
                          additionalSoInfo[production.salesOrderId])) && (
                          <Typography>
                            {production.salesOrderDisplayId ||
                              production.salesOrderNumber ||
                              production.otDisplayId ||
                              (production.salesOrderId !== null ? additionalSoInfo[production.salesOrderId] : '')}
                          </Typography>
                        )}
                    </TableCell>
                    <TableCell>
                      <Typography>
                        {production.premixId} - {production.premixName}
                      </Typography>

                      {production.batches.map((batch) => (
                        <Box key={batch.batchNumber} mb={2}>
                          <Typography>
                            <Box component="span" fontWeight="bold" display="inline">
                              No. Bets {cleanCorruptedString(batch.batchNumber)}
                            </Box>{' '}
                          </Typography>
                          {production.packagingId && (
                            <Typography>
                              Kode Set Kemasan: {production.packagingId}
                              {production.packagingName
                                ? ` - ${cleanCorruptedString(production.packagingName)}`
                                : ''}
                            </Typography>
                          )}

                          <Typography>
                            Jumlah Ruahan: {formatDecimalAuto(batch.quantity ?? 0)}kg
                          </Typography>

                          {batch.fillingAmount && (
                            <Typography>
                              Filling Amount: {formatDecimalAuto(batch.fillingAmount)}
                              {batch.fillingUnit}
                            </Typography>
                          )}
                          {batch.packagingAmount && (
                            <Typography>
                              Jumlah Kemasan Diminta: {formatDecimalAuto(batch.packagingAmount)}pcs
                            </Typography>
                          )}
                          <Box>
                            <Grid container spacing={2}>
                              <Grid item xs={6}>
                                <Button
                                  aria-label="create"
                                  color="primary"
                                  variant="contained"
                                  fullWidth
                                  onClick={() => {
                                    setProductionData(production)
                                    setSelectedProductionBatch(batch)
                                    setIsConfirmingPremixRequest(true)
                                  }}
                                  disabled={
                                    isComplete ||
                                    checkIfStockCardExists(
                                      production,
                                      STOCK_CARD_TYPE_PREMIX,
                                      batch.batchNumber,
                                      VALUE_OUT
                                    )
                                  }
                                >
                                  {getHalfCompletePremixButtonText(
                                    production,
                                    batch.batchNumber
                                  )}
                                </Button>
                              </Grid>
                              <Grid item xs={6}>
                                <Button
                                  aria-label="create"
                                  color="primary"
                                  variant="contained"
                                  fullWidth
                                  onClick={() => {
                                    setProductionData(production)
                                    setSelectedProductionBatch(batch)
                                    setIsConfirmingPackagingRequest(true)
                                  }}
                                  disabled={
                                    isComplete ||
                                    checkIfStockCardExists(
                                      production,
                                      STOCK_CARD_TYPE_PACKAGING,
                                      batch.batchNumber,
                                      VALUE_OUT
                                    )
                                  }
                                >
                                  {getHalfCompletePackagingButtonText(
                                    production,
                                    batch.batchNumber
                                  )}
                                </Button>
                              </Grid>
                            </Grid>
                          </Box>
                        </Box>
                      ))}
                    </TableCell>
                    <TableCell>
                      <Typography>{production.notes || '-'}</Typography>
                    </TableCell>

                    <TableCell>
                      <IconButton
                        aria-label="delete"
                        color="secondary"
                        onClick={() => {
                          setIsConfirmDeleteOpen(true)
                          setProductionData(production)
                        }}
                        size="large">
                        <ClearIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      </Paper>
      <ConfirmDialog
        title="Konfirmasi Minta Keluarkan Ruahan"
        open={isConfirmingPremixRequest}
        onConfirm={handleCreatePremixRequest}
        setOpen={setIsConfirmingPremixRequest}
      />
      <ConfirmDialog
        title="Konfirmasi Minta Keluarkan Kemasan"
        open={isConfirmingPackagingRequest}
        onConfirm={handleCreatePackagingRequest}
        setOpen={setIsConfirmingPackagingRequest}
      />
      <Snackbar
        open={isSuccessSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <MuiAlert severity="success" elevation={6} variant="filled">
          {alertMessage}
        </MuiAlert>
      </Snackbar>
      <Snackbar
        open={isFailureSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <MuiAlert severity="error" elevation={6} variant="filled">
          {alertMessage}
        </MuiAlert>
      </Snackbar>
      <Dialog
        fullWidth
        maxWidth="xl"
        open={isRequestingAdditionalPackagingOpen}
        onClose={() => {
          setIsRequestingAdditionalPackagingOpen(false)
          setProductionData(new Production({}))
        }}
      >
        <Box p={4}>
          <Typography variant="h4">Request Kemasan Tambahan</Typography>
          <Box mt={2} mb={2}>
            <TextField
              variant="outlined"
              fullWidth
              type="number"
              label="Jumlah Diminta"
              value={additionalPackagingAmount}
              onChange={(e) => setAdditionalPackagingAmount(e.target.value)}
            />
          </Box>
          <Box mb={2}>
            <TextField
              variant="outlined"
              fullWidth
              label="Alasan Diminta"
              value={additionalPackagingReason}
              onChange={(e) => setAdditionalPackagingReason(e.target.value)}
            />
          </Box>
          <Box>
            <Button
              variant="contained"
              color="primary"
              onClick={handleRequestAdditionalPackaging}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Dialog>
      <Dialog
        fullWidth
        maxWidth="xl"
        open={isRequestingAdditionalPremixOpen}
        onClose={() => {
          setIsRequestingAdditionalPremixOpen(false)
          setProductionData(new Production({}))
        }}
      >
        <Box p={4}>
          <Typography variant="h4">Request Ruahan Tambahan</Typography>
          <Box mt={4} mb={8}>
            <Typography variant="h6">Pilih Bets</Typography>
            <LoadBatchAutocomplete
              onChange={(e) => setSelectedBatch(e)}
              premixId={productionData.premixId}
              showDetailedTable
            />
          </Box>
          <Box mt={2} mb={2}>
            <Typography>Bets Dipilih: {selectedBatch?.batchNumber}</Typography>
          </Box>
          <Box mt={2} mb={2}>
            <TextField
              variant="outlined"
              fullWidth
              type="number"
              label="Jumlah Diminta"
              value={additionalPremixAmount}
              onChange={(e) => setAdditionalPremixAmount(e.target.value)}
            />
          </Box>
          <Box mb={2}>
            <TextField
              variant="outlined"
              fullWidth
              label="Alasan Diminta"
              value={additionalPremixReason}
              onChange={(e) => setAdditionalPremixReason(e.target.value)}
            />
          </Box>
          <Button
            variant="contained"
            color="primary"
            onClick={handleRequestAdditionalPremix}
          >
            Save
          </Button>
        </Box>
      </Dialog>
      <ConfirmDialog
        title={isComplete ? 'Konfirmasi Batal Selesai' : 'Konfirmasi Hapus'}
        open={isConfirmDeleteOpen}
        setOpen={setIsConfirmDeleteOpen}
        onConfirm={() => handleDeleteProduction()}
        confirmDisabled={productionData.stockCards?.some((sc) => sc.isConfirmed)}
      >
        <Typography variant="h6">
          {isComplete
            ? `Yakin ingin membatalkan status SELESAI untuk ${productionData.productionDisplayId}? Form akan dikembalikan ke tab AKTIF.`
            : `Yakin ingin menghapus ${productionData.productionDisplayId} dari system?`}
        </Typography>
        <Box mt={2}>
          <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
            {isComplete ? 'Kartu Stok yang akan dibatalkan:' : 'Kartu Stok yang akan dihapus:'}
          </Typography>
          {productionData.stockCards && productionData.stockCards.length > 0 ? (
            <Box mt={1}>
              {productionData.stockCards.map((stockCard, index) => (
                <Box key={index} mb={1} pl={2}>
                  <Typography>
                    • {stockCard.stockCardDisplayId || 'ID belum tersedia'} - {' '}
                    {getStockCardDescription(stockCard, productionData)}
                    {Boolean(stockCard.isConfirmed) && ' (Sudah dikonfirmasi)'}
                  </Typography>
                </Box>
              ))}
              {productionData.stockCards.some((sc) => sc.isConfirmed) && (
                <Box mt={2}>
                  <Typography color="error" style={{ fontWeight: 'bold' }}>
                    {isComplete
                      ? 'Tidak dapat membatalkan: Ada kartu stok yang sudah dikonfirmasi.'
                      : 'Tidak dapat menghapus: Ada kartu stok yang sudah dikonfirmasi.'}
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Typography color="textSecondary" style={{ marginTop: 8 }}>
              Tidak ada kartu stok yang terkait.
            </Typography>
          )}
        </Box>
      </ConfirmDialog>
    </Box>
  );
}
